import { useEffect, memo, useMemo, useState, type MouseEvent } from 'react';
import DOMPurify from 'dompurify';
import {
  fetchNotifications,
  dismissCriticalNotification,
} from 'services/misc/miscAsync';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  getCriticalNotifications,
  getCriticalNotificationsLoadingStatus,
  setCriticalNotificationsData,
} from 'services/misc/criticalNotificationSlice';
import {
  DISCOVER_PROPRIETARY_NOTIFICATION_ID,
  DISCOVER_PROPRIETARY_NOTIFICATION_MESSAGE,
  DISCOVER_PROPRIETARY_NOTIFICATION_TYPE,
} from 'services/misc/criticalNotificationConfig';
import { selectIsDiscoverProprietaryUpdateNeeded } from 'services/ctp/ctpSelectors';
import Alert from 'components/Alert';
import LazySvg from 'components/LazySvg';
import If from 'components/If';
import { logErrors } from 'utils';
import {
  selectIsEligibleForDataCollection,
  selectIsDataCollectionBannerClosed,
} from 'services/misc/miscSelectors';
import { requestCardExpiryModalOnPayment } from 'services/misc/miscSlice';
import DataCollectionBanner from 'pages/DataCollectionModal/banner';
import announceLayoutShift from 'utils/layoutShift';
import styles from './index.module.scss';

// type
export type CriticalNotificationType = {
  id: string;
  message: string;
  notification: {
    /* BE flag — when true the notification is persisted on the
     * server and the client must call `PUT /notifications/{id}` to
     * dismiss it; when false the notification is transient and only
     * the local list needs updating. */
    userDeactivatable?: boolean;
    type?: string;
    severity?: string;
  };
};

function CriticalNotification() {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(getCriticalNotifications);
  const isNotificationsLoaded = useAppSelector(
    getCriticalNotificationsLoadingStatus,
  );
  const isEligibleForDataCollection = useAppSelector(
    selectIsEligibleForDataCollection,
  );
  const isDataCollectionBannerClosed = useAppSelector(
    selectIsDataCollectionBannerClosed,
  );
  /* Angular parity — `critical-notifications.component.ts:126-138
   * checkProprietaryUser`. Discover-employee users with no Zuora
   * account see a client-synth banner. Kept out of the store so it
   * appears the moment the ctp billing-account fetch reports
   * `accountFound: false`, regardless of notifications-fetch order.
   * The billing-account fetch itself is owned by
   * `useCardExpiryReminder` (layout-mounted via CardExpiryOnDemand)
   * — no fetch is needed here. */
  const showDiscoverProprietaryBanner = useAppSelector(
    selectIsDiscoverProprietaryUpdateNeeded,
  );
  const [
    isDiscoverProprietaryBannerDismissed,
    setIsDiscoverProprietaryBannerDismissed,
  ] = useState(false);

  // fetch critical notifications
  useEffect(() => {
    async function getNotifications() {
      try {
        await dispatch(fetchNotifications());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isNotificationsLoaded) {
      getNotifications();
    }
  }, []);

  /* Compose the display list: BE notifications + optional
   * client-synth Discover-employee banner. Client-synth is prepended
   * so it renders first (Angular's `checkProprietaryUser` dispatches
   * as a top-priority custom notification). Kept in the render, not
   * the store, so the ordering stays correct even if the ctp fetch
   * lands after the notifications fetch. */
  const displayNotifications = useMemo(() => {
    const list: CriticalNotificationType[] = [...notifications];
    if (
      showDiscoverProprietaryBanner &&
      !isDiscoverProprietaryBannerDismissed
    ) {
      list.unshift({
        id: DISCOVER_PROPRIETARY_NOTIFICATION_ID,
        message: DISCOVER_PROPRIETARY_NOTIFICATION_MESSAGE,
        notification: {
          type: DISCOVER_PROPRIETARY_NOTIFICATION_TYPE,
          severity: 'error',
          userDeactivatable: false,
        },
      });
    }
    return list;
  }, [
    notifications,
    showDiscoverProprietaryBanner,
    isDiscoverProprietaryBannerDismissed,
  ]);

  /* This slot renders at the top of the content column, so whatever it shows
     moves every dashboard section below it. Both the PII banner and the
     notification list arrive from async fetches (and one can replace the other,
     changing the height again), all after the page has settled — so anything
     measuring the layout has to be told. See `utils/layoutShift`. Keyed on what
     is rendered rather than on the notification payloads, so it fires only when
     the height actually changes. */
  const visibleBanner = useMemo(() => {
    if (isEligibleForDataCollection && !isDataCollectionBannerClosed)
      return 'pii';
    /* The count is part of the key, not just the branch: the alerts stack, so
       dismissing one of several changes the height without changing which
       branch renders. */
    return displayNotifications.length
      ? `notifications:${displayNotifications.length}`
      : 'none';
  }, [
    isEligibleForDataCollection,
    isDataCollectionBannerClosed,
    displayNotifications.length,
  ]);

  useEffect(() => announceLayoutShift(), [visibleBanner]);

  /* Angular parity — `critical-notifications.component.ts:183-190`.
   * Dismissing a notification takes one of two paths depending on
   * the BE's `userDeactivatable` flag:
   *   – userDeactivatable=true  → PUT /notifications/{id}, BE
   *     tombstones the row so it doesn't come back on the next
   *     fetch. We also remove it locally to give an instant response.
   *   – userDeactivatable=false → local removal only. These are
   *     things like the browser-unsupported banner that live only
   *     on the client and would come right back from the BE. */
  const handleNotificationClose = (item: CriticalNotificationType) => {
    /* Client-synth banners are not in the store — hide via local
     * component state so they don't reappear on every render pass. */
    if (item?.id === DISCOVER_PROPRIETARY_NOTIFICATION_ID) {
      setIsDiscoverProprietaryBannerDismissed(true);
      return;
    }
    if (item?.notification?.userDeactivatable && item?.id) {
      dispatch(dismissCriticalNotification(item.id)).catch(logErrors);
    }
    const updatedNotifications = notifications.filter((n) => n.id !== item.id);
    dispatch(setCriticalNotificationsData(updatedNotifications));
  };

  if (isEligibleForDataCollection && !isDataCollectionBannerClosed) {
    return (
      <div className={styles.bannerContainer}>
        <DataCollectionBanner />
      </div>
    );
  }

  /* Angular parity — `critical-notifications.component.html:30`
   * `(click)="updatePayment()"`. The Discover proprietary banner's
   * link opens the Card Expiry Reminder modal (proprietary variant)
   * instead of navigating. Intercepts the anchor click inside the
   * sanitised HTML and dispatches a store flag; the dashboard hook
   * (or any layout-level consumer) opens the modal in response. */
  const handleMessageClick = (
    event: MouseEvent<HTMLSpanElement>,
    item: CriticalNotificationType,
  ) => {
    if (item?.id !== DISCOVER_PROPRIETARY_NOTIFICATION_ID) return;
    const target = event.target as HTMLElement | null;
    if (!target?.closest('a')) return;
    event.preventDefault();
    /* Angular passes `showZuoraForm: true` on this open, skipping
     * the reminder step — the banner already gave the reason. */
    dispatch(requestCardExpiryModalOnPayment());
  };

  return (
    <If condition={!!displayNotifications.length}>
      <div className={styles.container}>
        {displayNotifications.map((item: CriticalNotificationType) => (
          <Alert
            key={item?.id}
            type='error'
            message={
              /* The interactive target inside is the anchor tag —
               * it already handles keyboard activation (Enter)
               * natively. This handler intercepts the click on that
               * anchor to open a modal instead of navigating; the
               * span itself isn't focusable or interactive. */
              /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
              <span
                onClick={(event) => handleMessageClick(event, item)}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(item?.message ?? ''),
                }}
              />
            }
            closable={{
              'aria-label': 'close',
              closeIcon: (
                <LazySvg
                  name='close'
                  className={styles.closeIcon}
                  onClick={() => handleNotificationClose(item)}
                />
              ),
            }}
            /* antd owns the close: it collapses and unmounts the alert
               internally, so this component does not necessarily re-render and
               the effect above cannot see it. `afterClose` is antd's own
               post-collapse hook, which makes the announcement independent of
               whether the Redux list was updated. */
            afterClose={() => {
              announceLayoutShift();
            }}
            className={styles.notification}
          />
        ))}
      </div>
    </If>
  );
}

export default memo(CriticalNotification);
